import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import {
  Plant as PlantIcon,
  ArrowLeft,
  ArrowRight,
  Camera,
  Bell,
  BellSlash,
  ChartLineUp,
  ThermometerHot,
  Drop,
  Sun,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const SEV_CLASS = {
  none: "severity-none",
  mild: "severity-mild",
  moderate: "severity-moderate",
  severe: "severity-severe",
  critical: "severity-critical",
};

export default function PlantDetail() {
  const { plantId } = useParams();
  const navigate = useNavigate();
  const [plant, setPlant] = useState(null);
  const [scans, setScans] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const PAGE = 20;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/plants/${plantId}/scans`, { params: { limit: PAGE, offset: 0 } });
        setPlant(data.plant);
        setScans(data.scans || []);
        setTotal(data.total || 0);
        setHasMore(!!data.has_more);
      } catch {
        toast.error("Plant not found");
        navigate("/plants");
      } finally {
        setLoading(false);
      }
    })();
  }, [plantId, navigate]);

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const { data } = await api.get(`/plants/${plantId}/scans`, { params: { limit: PAGE, offset: scans.length } });
      setScans((arr) => [...arr, ...(data.scans || [])]);
      setHasMore(!!data.has_more);
      setTotal(data.total || total);
    } catch {
      toast.error("Couldn't load more");
    } finally {
      setLoadingMore(false);
    }
  };

  const toggleAlerts = async () => {
    try {
      const { data } = await api.patch(`/plants/${plantId}`, { alerts_enabled: !plant.alerts_enabled });
      setPlant(data);
    } catch {
      toast.error("Couldn't update");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-sand-50">
        <Navbar />
        <main className="max-w-5xl mx-auto px-6 py-12 font-mono-data text-mute animate-pulse-soft">loading plant…</main>
      </div>
    );
  }
  if (!plant) return null;

  const t = plant.thresholds || {};
  const severityCounts = scans.reduce((acc, s) => {
    const k = (s.primary_diagnosis?.severity || "unknown").toLowerCase();
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
  const avgRisk = scans.length
    ? Math.round(
        scans.reduce((sum, s) => sum + (s.climate_contribution?.climate_risk_score || 0), 0) / scans.length
      )
    : null;

  return (
    <div className="min-h-screen bg-sand-50" data-testid="plant-detail-page">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 lg:px-10 py-10 space-y-8">
        <Link to="/plants" className="btn-secondary !text-sm inline-flex" data-testid="back-to-plants"><ArrowLeft size={14} /> Back to plants</Link>

        <div className="grid md:grid-cols-12 gap-6">
          <div className="md:col-span-5">
            <div className="aspect-square rounded-3xl overflow-hidden bg-forest/10 flex items-center justify-center border border-sand-200">
              {plant.thumbnail_b64 ? (
                <img src={`data:image/jpeg;base64,${plant.thumbnail_b64}`} alt={plant.common_name} className="w-full h-full object-cover" />
              ) : (
                <PlantIcon size={80} weight="duotone" className="text-forest/60" />
              )}
            </div>
          </div>
          <div className="md:col-span-7 space-y-4">
            <div>
              <div className="font-mono-data text-mute">{t.category || "plant"}</div>
              <h1 className="font-display text-4xl md:text-5xl text-ink leading-tight" data-testid="plant-name">
                {plant.nickname || plant.common_name}
              </h1>
              {plant.scientific_name && (
                <div className="italic text-mute">{plant.scientific_name}</div>
              )}
            </div>
            <button onClick={toggleAlerts} className={`btn-secondary !text-sm`} data-testid="toggle-alerts">
              {plant.alerts_enabled ? <Bell size={14} /> : <BellSlash size={14} />}
              {plant.alerts_enabled ? " Alerts ON" : " Alerts off"}
            </button>

            <div className="bg-sand-100 border border-sand-200 rounded-2xl p-5">
              <div className="font-mono-data text-mute mb-3">AI thresholds</div>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <Mini label="Temp" value={`${t.temp_c_min}–${t.temp_c_max}°C`} Icon={ThermometerHot} />
                <Mini label="Humidity" value={`${t.humidity_pct_min}–${t.humidity_pct_max}%`} Icon={Drop} />
                <Mini label="UV max" value={t.uv_index_max} Icon={Sun} />
              </div>
              {t.notes && <p className="text-mute text-sm mt-3 italic">{t.notes}</p>}
            </div>
          </div>
        </div>

        {scans.length > 0 && (
          <div className="grid sm:grid-cols-3 gap-4">
            <Stat label="Scans" value={scans.length} Icon={ChartLineUp} />
            <Stat label="Avg climate risk" value={avgRisk != null ? `${avgRisk}/100` : "—"} Icon={ThermometerHot} />
            <Stat
              label="Severities"
              value={
                <span className="flex flex-wrap gap-1">
                  {Object.entries(severityCounts).map(([k, v]) => (
                    <span key={k} className={`tag ${SEV_CLASS[k] || "severity-mild"}`}>{k} {v}</span>
                  ))}
                </span>
              }
              Icon={PlantIcon}
            />
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-mono-data text-mute">timeline</div>
              <h2 className="font-display text-3xl text-ink leading-tight">Repeat-scan history</h2>
            </div>
            <Link to="/scan" className="btn-primary" data-testid="plant-new-scan"><Camera size={14} weight="bold" /> Scan this plant</Link>
          </div>
          {scans.length === 0 ? (
            <div className="bg-sand-100 border border-sand-200 rounded-3xl p-10 text-center" data-testid="plant-empty-timeline">
              <PlantIcon size={40} weight="duotone" className="text-forest mx-auto mb-3" />
              <h3 className="font-display text-2xl">No scans for this plant yet</h3>
              <p className="text-mute mt-1">When you scan, pick this plant from the dropdown — its diagnoses will gather here.</p>
              <Link to="/scan" className="btn-primary mt-5 inline-flex"><Camera size={14} /> Start a scan</Link>
            </div>
          ) : (
            <ol className="relative border-l border-sand-200 ml-3 space-y-5" data-testid="plant-timeline">
              {scans.map((s) => (
                <li key={s.scan_id} className="ml-6">
                  <span className="absolute -left-2 mt-2 w-3 h-3 rounded-full bg-forest border-2 border-sand-50" />
                  <Link
                    to={`/scan/${s.scan_id}`}
                    className="block bg-sand-100 border border-sand-200 rounded-2xl p-4 hover:bg-white transition"
                  >
                    <div className="flex items-start gap-4">
                      {s.thumbnail_b64 && (
                        <img
                          src={`data:image/jpeg;base64,${s.thumbnail_b64}`}
                          alt="scan"
                          className="w-20 h-20 rounded-xl object-cover border border-sand-200 shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-display text-xl text-ink truncate">
                            {s.primary_diagnosis?.disorder || "Inconclusive"}
                          </h3>
                          <ArrowRight size={14} weight="bold" className="text-forest mt-1 shrink-0" />
                        </div>
                        <div className="flex flex-wrap gap-2 mt-1">
                          <span className={`tag ${SEV_CLASS[(s.primary_diagnosis?.severity || "mild").toLowerCase()]}`}>
                            {s.primary_diagnosis?.severity || "—"}
                          </span>
                          {s.climate_contribution?.climate_risk_score != null && (
                            <span className="tag">climate {s.climate_contribution.climate_risk_score}</span>
                          )}
                          {s.climate?.temperature_c != null && (
                            <span className="tag">{s.climate.temperature_c}°C</span>
                          )}
                          {s.climate?.humidity_pct != null && (
                            <span className="tag">{s.climate.humidity_pct}% rh</span>
                          )}
                        </div>
                        <div className="font-mono-data text-mute text-xs mt-2">
                          {new Date(s.created_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ol>
          )}
          {hasMore && (
            <div className="text-center mt-6">
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="btn-secondary !text-sm"
                data-testid="plant-load-more"
              >
                {loadingMore ? "Loading…" : `Load more (${scans.length}/${total})`}
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function Mini({ label, value, Icon }) {
  return (
    <div className="bg-white/70 border border-sand-200 rounded-xl p-3">
      <div className="flex items-center gap-1 text-mute"><Icon size={12} weight="duotone" /><span className="font-mono-data">{label}</span></div>
      <div className="font-display text-xl text-ink leading-tight mt-1">{value ?? "—"}</div>
    </div>
  );
}

function Stat({ label, value, Icon }) {
  return (
    <div className="bg-sand-100 border border-sand-200 rounded-2xl p-5 flex items-center gap-4">
      <div className="w-12 h-12 rounded-2xl bg-forest text-white flex items-center justify-center shrink-0">
        <Icon size={22} weight="duotone" />
      </div>
      <div className="min-w-0">
        <div className="font-mono-data text-mute">{label}</div>
        <div className="font-display text-2xl text-ink leading-tight">{value}</div>
      </div>
    </div>
  );
}
